<template>
  <div>
    <b-row>
      <b-col>
        <b-card class="header-marcar_consulta bg-gradient-primary">
          <b-row align-v="center">
            <b-col lg="4">
              <h1>
                <feather-icon icon="MapPinIcon" size="30" class="mr-50" />
                Endereços dos Alunos
              </h1>
            </b-col>
            <b-col lg="5">
              <b-form @submit.prevent="fetchEnderecos">
                <b-input-group class="input-group-merge">
                  <b-input-group-prepend is-text>
                    <feather-icon icon="SearchIcon" />
                  </b-input-group-prepend>
                  <b-form-input
                    v-model="filtro.search"
                    placeholder="Buscar por aluno, código, rua, bairro ou CEP"
                    @input="onSearchInput"
                  />
                </b-input-group>
              </b-form>
            </b-col>
            <b-col lg="3" class="text-right">
              <b-button variant="primary button-right" :to="{ name: 'AlunoEnderecoCadastrar' }">
                <feather-icon icon="PlusIcon" class="mr-50" />
                Novo Endereço
              </b-button>
            </b-col>
          </b-row>
        </b-card>
      </b-col>
    </b-row>

    <!-- Filtros Secundários -->
    <b-card class="mb-2">
      <b-row>
        <b-col md="4" class="mb-1 mb-md-0">
          <label class="font-small-3 font-weight-bold">Escola</label>
          <v-select
            v-model="filtro.schoolId"
            :options="escolaOptions"
            :reduce="option => option.value"
            label="text"
            placeholder="Todas as escolas"
            @input="onEscolaChange"
          />
        </b-col>
        <b-col md="4" class="mb-1 mb-md-0">
          <label class="font-small-3 font-weight-bold">Turma</label>
          <v-select
            v-model="filtro.classroomId"
            :options="turmaOptions"
            :reduce="option => option.value"
            label="text"
            placeholder="Todas as turmas"
            :disabled="!filtro.schoolId"
            @input="fetchEnderecos"
          />
        </b-col>
        <b-col md="4" class="d-flex align-items-end">
          <b-button variant="outline-secondary" class="mr-1" @click="limparFiltros">
            Limpar Filtros
          </b-button>
          <b-button variant="primary" @click="fetchEnderecos">
            Filtrar
          </b-button>
        </b-col>
      </b-row>
    </b-card>

    <div class="d-flex mb-1">
      <div class="align-self-center text-muted">
        Total: <strong>{{ fetchedData.totalItems || 0 }}</strong> endereço(s)
      </div>
      <b-pagination
        @input="onChangePage"
        class="ml-auto mb-0"
        v-model="currentPage"
        :total-rows="fetchedData.totalItems || 0"
        :per-page="perPage"
      ></b-pagination>
    </div>

    <b-card no-body>
      <b-table
        ref="refTable"
        class="position-relative"
        :items="fetchedData.content"
        responsive
        :fields="tableColumns"
        primary-key="id"
        show-empty
        empty-text="Nenhum endereço de aluno cadastrado."
      >
        <!-- Coluna Aluno -->
        <template #cell(nomeAluno)="data">
          <div>
            <span class="font-weight-bolder text-primary d-block">{{ data.item.nomeAluno }}</span>
            <small v-if="data.item.nomeResponsavel" class="text-muted">
              Resp: {{ data.item.nomeResponsavel }}
            </small>
          </div>
        </template>

        <!-- Coluna Escola / Turma -->
        <template #cell(escolaNome)="data">
          <div>
            <span class="d-block font-weight-bold">{{ data.item.escolaNome || 'Escola não informada' }}</span>
            <small class="text-muted">{{ data.item.turmaNome || data.item.turmaTexto || 'Turma não informada' }}</small>
          </div>
        </template>

        <!-- Coluna Código Domicílio -->
        <template #cell(codigoDomicilio)="data">
          <b-badge variant="light-primary" class="font-weight-bold">
            {{ data.item.codigoDomicilio || '-' }}
          </b-badge>
        </template>

        <!-- Coluna Endereço -->
        <template #cell(endereco)="data">
          <div>
            <span>{{ data.item.endereco }}<template v-if="data.item.numero">, {{ data.item.numero }}</template></span>
            <small v-if="data.item.complemento" class="d-block text-muted">Comp: {{ data.item.complemento }}</small>
            <small class="d-block text-muted">
              {{ data.item.bairro }}<template v-if="data.item.municipio"> - {{ data.item.municipio }}</template>
              <template v-if="data.item.cep"> ({{ data.item.cep }})</template>
            </small>
            <small v-if="data.item.pontoReferencia" class="d-block text-info">
              Ref: {{ data.item.pontoReferencia }}
            </small>
          </div>
        </template>

        <!-- Coluna Telefone -->
        <template #cell(telefone)="data">
          <span>{{ data.item.telefone || '-' }}</span>
        </template>

        <!-- Coluna Ações -->
        <template #cell(actions)="data">
          <b-dropdown
            variant="link"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item
              :to="{ name: 'AlunoEnderecoEditar', params: { id: data.item.id } }"
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">Editar</span>
            </b-dropdown-item>
            <b-dropdown-item
              variant="danger"
              @click="confirmDelete(data.item)"
            >
              <feather-icon icon="TrashIcon" />
              <span class="align-middle ml-50">Excluir</span>
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </b-table>
    </b-card>

    <div class="d-flex mt-1">
      <b-pagination
        @input="onChangePage"
        class="ml-auto"
        v-model="currentPage"
        :total-rows="fetchedData.totalItems || 0"
        :per-page="perPage"
      ></b-pagination>
    </div>
  </div>
</template>

<script>
import alunoEnderecoService from '@/services/alunoEnderecoService'
import schoolService from '@/services/schoolService'
import classroomService from '@/services/classroomService'
import { hideSpinner, showSpinner } from '@/services/spinService'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import {
  BRow, BCol, BCard, BButton, BTable, BDropdown, BDropdownItem,
  BPagination, BForm, BFormInput, BInputGroup, BInputGroupPrepend, BBadge
} from 'bootstrap-vue'
import vSelect from 'vue-select'

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BButton,
    BTable,
    BDropdown,
    BDropdownItem,
    BPagination,
    BForm,
    BFormInput,
    BInputGroup,
    BInputGroupPrepend,
    BBadge,
    vSelect,
  },
  data() {
    return {
      filtro: {
        search: '',
        schoolId: null,
        classroomId: null,
      },
      currentPage: 1,
      perPage: 10,
      fetchedData: {
        content: [],
        totalItems: 0,
      },
      escolaOptions: [],
      turmaOptions: [],
      debounceTimer: null,
      tableColumns: [
        { key: 'nomeAluno', label: 'Aluno', sortable: true },
        { key: 'escolaNome', label: 'Escola / Turma', sortable: true },
        { key: 'codigoDomicilio', label: 'Cód. Domicílio', sortable: true },
        { key: 'endereco', label: 'Endereço Completo', sortable: false },
        { key: 'telefone', label: 'Contato', sortable: false },
        { key: 'actions', label: 'Ações', sortable: false },
      ],
    }
  },
  mounted() {
    this.carregarEscolas()
    this.fetchEnderecos()
  },
  methods: {
    async carregarEscolas() {
      try {
        const response = await schoolService.getAll({ page: 1, itemsPerPage: 100 })
        const list = response.content || response || []
        this.escolaOptions = list.map(e => ({ value: e.id, text: e.name }))
      } catch (error) {
        console.error('Erro ao carregar escolas:', error)
      }
    },
    formatarNomeTurma(c) {
      if (!c) return ''
      const turma = (c.turma || '').trim()
      const ano = (c.ano || '').trim()
      const turno = (c.turno || '').trim()

      if (/ano|º|ª/i.test(turma)) {
        return turno ? `${turma} - ${turno}` : turma
      }

      if (/ano|º|ª/i.test(ano)) {
        const nome = turma ? `${ano} ${turma}` : ano
        return turno ? `${nome} - ${turno}` : nome
      }

      if (/^\d+$/.test(ano)) {
        const nome = turma ? `${ano}º ano ${turma}` : `${ano}º ano`
        return turno ? `${nome} - ${turno}` : nome
      }

      const partes = [ano, turma].filter(Boolean).join(' ')
      return turno ? `${partes} - ${turno}` : partes
    },
    async onEscolaChange(schoolId) {
      this.filtro.classroomId = null
      this.turmaOptions = []
      if (schoolId) {
        try {
          const res = await classroomService.getAll({ schoolId, page: 1, itemsPerPage: 100 })
          const list = res.content || res || []
          this.turmaOptions = list.map(c => ({
            value: c.id,
            text: this.formatarNomeTurma(c),
          }))
        } catch (error) {
          console.error('Erro ao carregar turmas:', error)
        }
      }
      this.fetchEnderecos()
    },
    onSearchInput() {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(() => {
        this.currentPage = 1
        this.fetchEnderecos()
      }, 400)
    },
    limparFiltros() {
      this.filtro = {
        search: '',
        schoolId: null,
        classroomId: null,
      }
      this.turmaOptions = []
      this.currentPage = 1
      this.fetchEnderecos()
    },
    onChangePage() {
      this.fetchEnderecos()
    },
    async fetchEnderecos() {
      showSpinner()
      try {
        const params = {
          page: this.currentPage,
          itemsPerPage: this.perPage,
          search: this.filtro.search || undefined,
          schoolId: this.filtro.schoolId || undefined,
          classroomId: this.filtro.classroomId || undefined,
        }
        const data = await alunoEnderecoService.getAll(params)
        this.fetchedData = data || { content: [], totalItems: 0 }
      } catch (error) {
        console.error('Erro ao buscar endereços:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao carregar lista de endereços',
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      } finally {
        hideSpinner()
      }
    },
    confirmDelete(item) {
      this.$bvModal
        .msgBoxConfirm(`Deseja realmente excluir o endereço de "${item.nomeAluno}"?`, {
          title: 'Confirmar Exclusão',
          size: 'sm',
          okVariant: 'danger',
          okTitle: 'Sim, excluir',
          cancelTitle: 'Cancelar',
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true,
        })
        .then(value => {
          if (value) {
            this.deleteEndereco(item.id)
          }
        })
    },
    async deleteEndereco(id) {
      showSpinner()
      try {
        await alunoEnderecoService.delete(id)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Endereço excluído com sucesso!',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
        this.fetchEnderecos()
      } catch (error) {
        const message = error.response?.data?.message || 'Erro ao excluir endereço.'
        this.$toast({
          component: ToastificationContent,
          props: {
            title: message,
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      } finally {
        hideSpinner()
      }
    },
  },
}
</script>
